/* eslint-disable no-undef */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchWithTimeout, clearApiCache } from './apiUtils';
import { mapFieldsToCamelCase } from './cardUtils';
import { mapActionsToCamelCase } from './actionUtils';
import { 
  fetchCarePlans, 
  createCarePlan, 
  updateCarePlan, 
  deleteCarePlan,
  fetchActionPlans,
  createActionPlan,
  updateActionPlan,
  deleteActionPlan,
  fetchActionTriggers,
  createActionTrigger,
  updateActionTrigger,
  deleteActionTrigger,
  fetchCarePlanEvents,
  fetchCarePlanSchedule,
  fetchWateringSummary,
  fetchWateringEvents,
  isWateringEventsEnabled
} from './apiUtils';

const CARDS_SERVER = process.env.REACT_APP_CARDS_SERVER;
const ACTIONS_SERVER = process.env.REACT_APP_ACTIONS_SERVER;
const DEVICES_SERVER = (CARDS_SERVER || '').replace(/\/api\/cards\/?$/i, '/api/devices/');

// Debug environment variables


// Cards API functions
const fetchCardsForUser = async (userKey) => {
  if (!userKey) {
    console.warn("fetchCards: No user key found");
    return [];
  }
  
  try {
  
    const response = await fetchWithTimeout(`${CARDS_SERVER}${userKey}`, { method: 'GET', skipCache: true });
    
    if (!response.ok) {
      throw new Error(`fetchCards failed with status: ${response.status}`);
    }
    
    const data = await response.json();
    const mappedData = mapFieldsToCamelCase(data);
    

    

    
    return mappedData;
  } catch (error) {
    console.error(`Error fetching card data for user ${userKey}:`, error);
    throw error; // Re-throw to let React Query handle the error
  }
};

const fetchCards = async ({ queryKey }) => {
  const userKey = queryKey[1] ?? getUserValue();
  return fetchCardsForUser(userKey);
};

const parseMutationResponse = async (response, operation) => {
  let data = null;
  try {
    data = await response.json();
  } catch {
    // Some legacy endpoints do not return JSON.
  }

  if (!response.ok) {
    const baseMessage = data?.error || `${operation} failed with status: ${response.status}`;
    const message = data?.details ? `${baseMessage}: ${data.details}` : baseMessage;
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return data;
};

const mutateDevice = async ({ server, userKey, method, macAddress, body, operation }) => {
  if (!userKey) {
    throw new Error(`${operation}: No user key found`);
  }

  const encodedUser = encodeURIComponent(userKey);
  const encodedMac = macAddress ? `/${encodeURIComponent(macAddress)}` : '';
  const response = await fetchWithTimeout(`${server}${encodedUser}${encodedMac}`, {
    method,
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  }, 10000);

  return parseMutationResponse(response, operation);
};

const isMethodNotAllowed = (error) => error?.status === 405;

const isAlreadyGone = (error) => error?.status === 404;

const refreshDeviceQueries = (queryClient, userKey) => {
  if (CARDS_SERVER) clearApiCache(CARDS_SERVER);
  if (ACTIONS_SERVER) clearApiCache(ACTIONS_SERVER);
  if (DEVICES_SERVER) clearApiCache(DEVICES_SERVER);
  queryClient.invalidateQueries({ queryKey: ['cards', userKey] });
  queryClient.invalidateQueries({ queryKey: ['actions', userKey] });
  queryClient.invalidateQueries({ queryKey: ['deviceHistory', userKey] });
};

const syncActionsLegacy = async (userKey, actions) => {
  const response = await fetchWithTimeout(`${ACTIONS_SERVER}update/${userKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(actions),
  }, 10000);
  return parseMutationResponse(response, 'updateActions');
};

const syncCardsLegacy = async (userKey, cards) => {
  const response = await fetchWithTimeout(`${CARDS_SERVER}update/${userKey}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cards),
  }, 10000);
  return parseMutationResponse(response, 'updateCards');
};

const fetchActionsForUser = async (userKey) => {
  if (!userKey) {
    console.warn("fetchActions: No user key found");
    return [];
  }
  
  try {
    console.log(`Fetching actions for user ${userKey}`);
    const response = await fetchWithTimeout(`${ACTIONS_SERVER}${userKey}`, { method: 'GET', skipCache: true });
    
    if (!response.ok) {
      throw new Error(`fetchActions failed with status: ${response.status}`);
    }
    
    const data = await response.json();
    let mappedData = mapActionsToCamelCase(data);
    
    // Process actions to ensure boolean values are correctly typed
    mappedData = mappedData.map(action => {
      // If tracking is a boolean true/false or string "true"/"false", normalize it
      let normalizedTracking = action.tracking;
      if (action.tracking === true || action.tracking === 'true') {
        normalizedTracking = true;
      } else if (action.tracking === false || action.tracking === 'false' || action.tracking === '0') {
        normalizedTracking = false;
      }
      // Otherwise, preserve the original string value (like "4d", "8h", etc.)
      
      return {
        ...action,
        pwm: action.pwm === 'true' || action.pwm === true || action.pwm === 1,
        tracking: normalizedTracking,
        // Convert sensorTracking from SQLite integer (0/1) to boolean
        sensorTracking: action.sensorTracking === 1 || action.sensorTracking === true || action.sensorTracking === '1'
      };
    });
    
    if (mappedData.length === 0) {
      console.warn(`No action data found for user: ${userKey}`);
    }
    
    return mappedData;
  } catch (error) {
    console.error(`Error fetching action data for user ${userKey}:`, error);
    throw error; // Re-throw to let React Query handle the error
  }
};

const fetchActions = async ({ queryKey } = {}) => {
  const userKey = queryKey?.[1] ?? getUserValue();
  return fetchActionsForUser(userKey);
};

const createActionItem = (userKey, action) => mutateDevice({
  server: ACTIONS_SERVER,
  userKey,
  method: 'POST',
  body: action,
  operation: 'createAction',
});

const createAction = async (userKey, action) => {
  try {
    return await createActionItem(userKey, action);
  } catch (error) {
    if (!isMethodNotAllowed(error)) {
      throw error;
    }

    const freshActions = await fetchActionsForUser(userKey);
    const isDuplicate = freshActions.some(
      (existing) => existing.macAddress === action.macAddress
    );
    if (isDuplicate) {
      throw new Error('Action with this MAC address already exists on the server');
    }

    await syncActionsLegacy(userKey, [...freshActions, action]);
  }
};

const patchActionItem = (userKey, macAddress, updates) => {
  console.log('[SENSOR_TRACKING] Updating one action:', {
    userKey,
    macAddress,
    duration: updates.duration,
    sensorTracking: updates.sensorTracking,
  });

  return mutateDevice({
    server: ACTIONS_SERVER,
    userKey,
    method: 'PATCH',
    macAddress,
    body: updates,
    operation: 'patchAction',
  });
};

const patchAction = async (userKey, macAddress, updates, currentActions) => {
  try {
    return await patchActionItem(userKey, macAddress, updates);
  } catch (error) {
    if (!isMethodNotAllowed(error)) {
      throw error;
    }

    const baseActions = currentActions ?? await fetchActionsForUser(userKey);
    const updated = baseActions.map((action) =>
      action.macAddress === macAddress ? { ...action, ...updates } : action
    );
    await syncActionsLegacy(userKey, updated);
  }
};

const deleteActionItem = (userKey, macAddress) => mutateDevice({
  server: ACTIONS_SERVER,
  userKey,
  method: 'DELETE',
  macAddress,
  operation: 'deleteAction',
});

const deleteAction = async (userKey, macAddress, currentActions) => {
  try {
    return await deleteActionItem(userKey, macAddress);
  } catch (error) {
    if (isAlreadyGone(error)) return;
    if (!isMethodNotAllowed(error)) {
      throw error;
    }

    const baseActions = currentActions ?? await fetchActionsForUser(userKey);
    const updated = baseActions.filter((action) => action.macAddress !== macAddress);
    await syncActionsLegacy(userKey, updated);
  }
};

const createCardItem = (userKey, card) => mutateDevice({
  server: CARDS_SERVER,
  userKey,
  method: 'POST',
  body: card,
  operation: 'createCard',
});

const createCard = async (userKey, card) => {
  try {
    return await createCardItem(userKey, card);
  } catch (error) {
    if (!isMethodNotAllowed(error)) {
      throw error;
    }

    const freshCards = await fetchCardsForUser(userKey);
    const isDuplicate = freshCards.some(
      (existing) => existing.macAddress === card.macAddress
    );
    if (isDuplicate) {
      throw new Error('Card with this MAC address already exists on the server');
    }

    await syncCardsLegacy(userKey, [...freshCards, card]);
  }
};

const patchCardItem = (userKey, macAddress, updates) => mutateDevice({
  server: CARDS_SERVER,
  userKey,
  method: 'PATCH',
  macAddress,
  body: updates,
  operation: 'patchCard',
});

const patchCard = async (userKey, macAddress, updates, currentCards) => {
  try {
    return await patchCardItem(userKey, macAddress, updates);
  } catch (error) {
    if (!isMethodNotAllowed(error)) {
      throw error;
    }

    const baseCards = currentCards ?? await fetchCardsForUser(userKey);
    const updated = baseCards.map((card) =>
      card.macAddress === macAddress ? { ...card, ...updates } : card
    );
    await syncCardsLegacy(userKey, updated);
  }
};

const deleteCardItem = (userKey, macAddress) => mutateDevice({
  server: CARDS_SERVER,
  userKey,
  method: 'DELETE',
  macAddress,
  operation: 'deleteCard',
});

const deleteCard = async (userKey, macAddress, currentCards) => {
  try {
    return await deleteCardItem(userKey, macAddress);
  } catch (error) {
    if (isAlreadyGone(error)) return;
    if (!isMethodNotAllowed(error)) {
      throw error;
    }

    const baseCards = currentCards ?? await fetchCardsForUser(userKey);
    const updated = baseCards.filter((card) => card.macAddress !== macAddress);
    await syncCardsLegacy(userKey, updated);
  }
};

const fetchDeviceHistory = async ({ queryKey } = {}) => {
  const userKey = queryKey?.[1] ?? getUserValue();
  if (!userKey) {
    return [];
  }

  const encodedUser = encodeURIComponent(userKey);
  const response = await fetchWithTimeout(`${DEVICES_SERVER}history/${encodedUser}`, {
    method: 'GET',
    skipCache: true,
  });
  return parseMutationResponse(response, 'fetchDeviceHistory') || [];
};

const restoreDeviceHistory = async (userKey, macAddress) => {
  if (!userKey) {
    throw new Error('restoreDevice: No user key found');
  }
  const encodedUser = encodeURIComponent(userKey);
  const encodedMac = encodeURIComponent(macAddress);
  const response = await fetchWithTimeout(
    `${DEVICES_SERVER}history/${encodedUser}/${encodedMac}/restore`,
    { method: 'POST' },
    10000
  );
  return parseMutationResponse(response, 'restoreDevice');
};

// React Query Hooks
// READ CARDS
export const useCards = (options = {}) => {
  // Extract refetchInterval from options if provided, otherwise default to 15 minutes
  const { refetchInterval: optionsRefetchInterval, userKey: optionsUserKey, ...restOptions } = options;

  // Ensure a minimum refetch interval of 1 minute even if overridden
  const refetchInterval = optionsRefetchInterval ? 
    Math.max(optionsRefetchInterval, 60000) : // At least 1 minute
    1000 * 60 * 15; // Default 15 minutes

  const userKey = optionsUserKey ?? getUserValue();

  return useQuery({
    queryKey: ['cards', userKey],
    queryFn: fetchCards,
    refetchInterval,
    refetchOnWindowFocus: false, // Disable to prevent unnecessary requests during form interactions
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes minimum
    enabled: !!userKey && (restOptions.enabled !== false),
    ...restOptions, // Pass through other options like refetchOnMount
  });
};

// READ ACTIONS
export const useActions = (options = {}) => {
  // Extract refetchInterval from options if provided, otherwise default to 15 minutes
  const { refetchInterval: optionsRefetchInterval, ...restOptions } = options;

  // Ensure a minimum refetch interval of 1 minute even if overridden
  const refetchInterval = optionsRefetchInterval ? 
    Math.max(optionsRefetchInterval, 60000) : // At least 1 minute
    1000 * 60 * 15; // Default 15 minutes

  const userKey = getUserValue();

  return useQuery({
    queryKey: ['actions', userKey],
    queryFn: fetchActions,
    refetchInterval,
    refetchOnWindowFocus: false, // Disable to prevent unnecessary requests during form interactions
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes minimum
    enabled: !!userKey, // Only fetch when user is available
    ...restOptions, // Pass through other options like 'enabled'
  });
};

// UPDATE ACTION
export function useUpdateActionMutation() {
  const queryClient = useQueryClient();
  const userKey = getUserValue();

  return useMutation({
    mutationFn: async (updatedAction) => {
      const current = queryClient.getQueryData(['actions', userKey]) || [];
      await patchAction(userKey, updatedAction.macAddress, updatedAction, current);
      return updatedAction;
    },
    onMutate: async (updatedAction) => {
      if (!updatedAction.macAddress) return;

      await queryClient.cancelQueries({ queryKey: ['actions', userKey] });
      const previousActions = queryClient.getQueryData(['actions', userKey]);

      const updated = previousActions?.map(action =>
        action.macAddress === updatedAction.macAddress ? {...action, ...updatedAction} 
        : action
      );

      queryClient.setQueryData(['actions', userKey], updated);

      return { previousActions }; // ✅ Needed for rollback in onError
    },
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    },
    onError: (err, _variables, context) => {
      if (context?.previousActions) {
        queryClient.setQueryData(['actions', userKey], context.previousActions);
      }
    }
  });
}

// UPDATE CARD
export function useUpdateCardMutation() {
  const queryClient = useQueryClient();
  const userKey = getUserValue();

  return useMutation({
    mutationFn: async (updatedCard) => {
      const current = queryClient.getQueryData(['cards', userKey]) || [];
      await patchCard(userKey, updatedCard.macAddress, updatedCard, current);
      return updatedCard;
    },
    onMutate: async (updatedCard) => {
      if (!updatedCard.macAddress) return;



      await queryClient.cancelQueries({ queryKey: ['cards', userKey] });
      const previousCards = queryClient.getQueryData(['cards', userKey]);

      const updated = previousCards?.map(card =>
        card.macAddress === updatedCard.macAddress ? {...card, ...updatedCard} 
        : card
      );

      queryClient.setQueryData(['cards', userKey], updated);



      return { previousCards }; // ✅ Needed for rollback in onError
    },
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    },
    onError: (err, _variables, context) => {
      if (context?.previousCards) {
        queryClient.setQueryData(['cards', userKey], context.previousCards);
      }
    }
  });
}


// BULK UPDATE ACTIONS
// Applies many per-action partial updates (keyed by macAddress) in a single
// optimistic cache write + a single backend POST, instead of one round-trip
// per device. Used by quick actions where a preset can touch many devices.
export function useBulkUpdateActionsMutation() {
  const queryClient = useQueryClient();
  const userKey = getUserValue();

  return useMutation({
    mutationFn: async (actionUpdates) => {
      const current = queryClient.getQueryData(['actions', userKey]) || [];
      try {
        await Promise.all(
          actionUpdates.map((action) => patchActionItem(userKey, action.macAddress, action))
        );
      } catch (error) {
        if (!isMethodNotAllowed(error)) {
          throw error;
        }
        await syncActionsLegacy(userKey, current);
      }
      return actionUpdates;
    },
    onMutate: async (actionUpdates) => {
      if (!Array.isArray(actionUpdates) || actionUpdates.length === 0) return;

      await queryClient.cancelQueries({ queryKey: ['actions', userKey] });
      const previousActions = queryClient.getQueryData(['actions', userKey]);

      const updateMap = new Map(actionUpdates.map((action) => [action.macAddress, action]));
      const updated = previousActions?.map(action =>
        updateMap.has(action.macAddress)
          ? { ...action, ...updateMap.get(action.macAddress) }
          : action
      );

      queryClient.setQueryData(['actions', userKey], updated);

      return { previousActions };
    },
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    },
    onError: (err, _variables, context) => {
      if (context?.previousActions) {
        queryClient.setQueryData(['actions', userKey], context.previousActions);
      }
    }
  });
}

// BULK UPDATE CARDS
export function useBulkUpdateCardsMutation() {
  const queryClient = useQueryClient();
  const userKey = getUserValue();

  return useMutation({
    mutationFn: async (cardUpdates) => {
      const current = queryClient.getQueryData(['cards', userKey]) || [];
      try {
        await Promise.all(
          cardUpdates.map((card) => patchCardItem(userKey, card.macAddress, card))
        );
      } catch (error) {
        if (!isMethodNotAllowed(error)) {
          throw error;
        }
        await syncCardsLegacy(userKey, current);
      }
      return cardUpdates;
    },
    onMutate: async (cardUpdates) => {
      if (!Array.isArray(cardUpdates) || cardUpdates.length === 0) return;

      await queryClient.cancelQueries({ queryKey: ['cards', userKey] });
      const previousCards = queryClient.getQueryData(['cards', userKey]);

      const updateMap = new Map(cardUpdates.map((card) => [card.macAddress, card]));
      const updated = previousCards?.map(card =>
        updateMap.has(card.macAddress)
          ? { ...card, ...updateMap.get(card.macAddress) }
          : card
      );

      queryClient.setQueryData(['cards', userKey], updated);

      return { previousCards };
    },
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    },
    onError: (err, _variables, context) => {
      if (context?.previousCards) {
        queryClient.setQueryData(['cards', userKey], context.previousCards);
      }
    }
  });
}

// ADD ACTION:
export const useAddAction = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (newAction) => {
      await createAction(userKey, newAction);
      return newAction;
    },
    onMutate: async (newAction) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['actions', userKey] });
      
      // Snapshot previous value
      const previousActions = queryClient.getQueryData(['actions', userKey]);
      
      // Optimistically update cache
      queryClient.setQueryData(['actions', userKey], old => {
        if (!old) return [newAction];
        return [...old, newAction];
      });
      
      return { previousActions };
    },
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    },
    onError: (err, newAction, context) => {
      // Roll back on error
      queryClient.setQueryData(['actions', userKey], context.previousActions);
    }
  });
};

// ADD CARD
export const useAddCard = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (newCard) => {
      await createCard(userKey, newCard);
      return newCard;
    },
    onMutate: async (newCard) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['cards', userKey] });
      
      // Snapshot the previous value
      const previousCards = queryClient.getQueryData(['cards', userKey]);
      
      // Optimistically update to the new value
      queryClient.setQueryData(['cards', userKey], old => {
        if (!old) return [newCard];
        return [...old, newCard];
      });
      
      return { previousCards };
    },
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    },
    onError: (err, newCard, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(['cards', userKey], context.previousCards);
    }
  });
};

/**
 * Hook for deleting a card with proper cache invalidation
 * @returns {UseMutationResult} Mutation hook for deleting cards
 */
export const useDeleteCard = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (macAddress) => {
      console.log('useDeleteCard: Starting deletion for MAC:', macAddress);
      const current = queryClient.getQueryData(['cards', userKey]) || [];
      await deleteCard(userKey, macAddress, current);
      console.log('useDeleteCard: Backend update successful');
      
      // Make sure to remove this address from MQTT tracking if it's an MQTT card
      if (macAddress.startsWith('MQ')) {
        // Note: This should be handled by the component calling this hook
        // but adding a log to ensure it's not forgotten
        console.info(`Deleted MAC ${macAddress} should also be removed from MQTT tracking`);
      }
      
      return macAddress;
    },
    onMutate: async (macAddress) => {
      console.log('useDeleteCard: onMutate - Starting optimistic update for MAC:', macAddress);
      if (!macAddress) return;

      await queryClient.cancelQueries({ queryKey: ['cards', userKey] });
      await queryClient.cancelQueries({ queryKey: ['actions', userKey] });
      const previousCards = queryClient.getQueryData(['cards', userKey]);
      const previousActions = queryClient.getQueryData(['actions', userKey]);
      console.log('useDeleteCard: onMutate - Previous cards count:', previousCards?.length || 0);

      const updated = previousCards?.filter(card => card.macAddress !== macAddress) || [];
      console.log('useDeleteCard: onMutate - Optimistic update, new count:', updated.length);

      if (previousCards) {
        queryClient.setQueryData(['cards', userKey], updated);
      }
      if (previousActions) {
        queryClient.setQueryData(
          ['actions', userKey],
          previousActions.filter((action) => action.sensor !== macAddress)
        );
      }

      return { previousCards, previousActions }; // ✅ Needed for rollback in onError
    },
    onError: (err, macAddress, context) => {
      if (isAlreadyGone(err)) return;
      console.error('useDeleteCard: onError - Rolling back deletion for MAC:', macAddress, err);
      if (context?.previousCards) {
        queryClient.setQueryData(['cards', userKey], context.previousCards);
      }
      if (context?.previousActions) {
        queryClient.setQueryData(['actions', userKey], context.previousActions);
      }
    },
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    }
  });
};
/**
 * Hook for deleting an action with proper cache invalidation
 * @returns {UseMutationResult} Mutation hook for deleting actions
 */
export const useDeleteAction = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (macAddress) => {
      console.log('useDeleteAction: Starting deletion for MAC:', macAddress);
      const current = queryClient.getQueryData(['actions', userKey]) || [];
      await deleteAction(userKey, macAddress, current);
      console.log('useDeleteAction: Backend update successful');
      
      return macAddress;
    },
    onMutate: async (macAddress) => {
      console.log('useDeleteAction: onMutate - Starting optimistic update for MAC:', macAddress);
      if (!macAddress) return;

      await queryClient.cancelQueries({ queryKey: ['actions', userKey] });
      const previousActions = queryClient.getQueryData(['actions', userKey]);
      console.log('useDeleteAction: onMutate - Previous actions count:', previousActions?.length || 0);

      const updated = previousActions?.filter(action => action.macAddress !== macAddress) || [];
      console.log('useDeleteAction: onMutate - Optimistic update, new count:', updated.length);

      if (previousActions) {
        queryClient.setQueryData(['actions', userKey], updated);
      }

      return { previousActions }; // ✅ Needed for rollback in onError
    },
    onError: (err, macAddress, context) => {
      if (isAlreadyGone(err)) return;
      console.error('useDeleteAction: onError - Rolling back deletion for MAC:', macAddress, err);
      if (context?.previousActions) {
        queryClient.setQueryData(['actions', userKey], context.previousActions);
      }
    },
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    }
  });
};

export const useDeviceHistory = (options = {}) => {
  const userKey = getUserValue();
  return useQuery({
    queryKey: ['deviceHistory', userKey],
    queryFn: fetchDeviceHistory,
    enabled: !!userKey,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useRestoreDevice = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();

  return useMutation({
    mutationFn: async (macAddress) => restoreDeviceHistory(userKey, macAddress),
    onSuccess: () => {
      refreshDeviceQueries(queryClient, userKey);
    },
  });
};

// ===== AUTOMATION REACT QUERY HOOKS =====

// READ CARE PLANS
export const useCarePlans = (filters = {}, options = {}) => {
  const userKey = getUserValue();
  
  return useQuery({
    queryKey: ['carePlans', userKey, filters],
    queryFn: () => {
      console.log('useCarePlans: Fetching data for userKey:', userKey, 'filters:', filters);
      return fetchCarePlans(userKey, filters);
    },
    enabled: !!userKey,
    refetchOnWindowFocus: false, // Disable to prevent unnecessary requests during form interactions
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// CREATE CARE PLAN
export const useCreateCarePlan = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (carePlanData) => {
      console.log('useCreateCarePlan mutation called with:', carePlanData);
      return createCarePlan(userKey, carePlanData);
    },
    onMutate: async (carePlanData) => {
      console.log('useCreateCarePlan: onMutate - Starting optimistic update');
      
      await queryClient.cancelQueries({ queryKey: ['carePlans', userKey] });
      const previousCarePlans = queryClient.getQueryData(['carePlans', userKey, {}]) || [];
      console.log('useCreateCarePlan: onMutate - Previous care plans count:', previousCarePlans.length);

      // Create a temporary care plan with a temporary ID
      const tempCarePlan = {
        care_plan_id: `temp-${Date.now()}`, // Temporary ID
        user_id: 0, // Will be set by server
        name: carePlanData.name,
        description: carePlanData.description || '',
        is_active: carePlanData.is_active || true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'user',
        updated_by: 'user',
        username: userKey
      };

      // Optimistically add the new care plan
      const updated = [...previousCarePlans, tempCarePlan];
      console.log('useCreateCarePlan: onMutate - Optimistic update applied, new count:', updated.length);

      queryClient.setQueryData(['carePlans', userKey, {}], updated);

      return { previousCarePlans, tempCarePlan }; // ✅ Needed for rollback in onError
    },
    onSuccess: (data, carePlanData, context) => {
      console.log('useCreateCarePlan: Success, replacing temp care plan with real one:', data);
      
      // Replace the temporary care plan with the real one from the server
      const currentCarePlans = queryClient.getQueryData(['carePlans', userKey, {}]) || [];
      const updated = currentCarePlans.map(plan => 
        plan.care_plan_id === context.tempCarePlan.care_plan_id 
          ? { ...plan, care_plan_id: data.care_plan_id }
          : plan
      );
      
      queryClient.setQueryData(['carePlans', userKey, {}], updated);
    },
    onError: (err, carePlanData, context) => {
      console.error('useCreateCarePlan: onError - Rolling back creation:', err);
      if (context?.previousCarePlans) {
        queryClient.setQueryData(['carePlans', userKey, {}], context.previousCarePlans);
      }
    }
  });
};

// UPDATE CARE PLAN
export const useUpdateCarePlan = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async ({ carePlanId, updateData }) => {
      console.log('useUpdateCarePlan mutation called with:', { carePlanId, updateData });
      return updateCarePlan(userKey, carePlanId, updateData);
    },
    onMutate: async ({ carePlanId, updateData }) => {
      console.log('useUpdateCarePlan: onMutate - Starting optimistic update for carePlanId:', carePlanId);
      if (!carePlanId) return;

      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['carePlans'] });
      
      // Get current data
      const previousCarePlans = queryClient.getQueryData(['carePlans', userKey, {}]);
      console.log('useUpdateCarePlan: onMutate - Previous care plans count:', previousCarePlans?.length || 0);

      // Optimistically update the care plan
      const updated = previousCarePlans?.map(plan => 
        plan.care_plan_id === carePlanId ? { ...plan, ...updateData } : plan
      ) || [];
      console.log('useUpdateCarePlan: onMutate - Optimistic update applied');

      // Update the cache
      queryClient.setQueryData(['carePlans', userKey, {}], updated);

      return { previousCarePlans }; // ✅ Needed for rollback in onError
    },
    onSuccess: () => {
      console.log('useUpdateCarePlan: Success - update completed');
    },
    onError: (err, variables, context) => {
      console.error('useUpdateCarePlan: onError - Rolling back update for carePlanId:', variables.carePlanId, err);
      if (context?.previousCarePlans) {
        queryClient.setQueryData(['carePlans', userKey, {}], context.previousCarePlans);
      }
    }
  });
};

// DELETE CARE PLAN
export const useDeleteCarePlan = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (carePlanId) => {
      console.log('useDeleteCarePlan mutation called with carePlanId:', carePlanId);
      return deleteCarePlan(userKey, carePlanId);
    },
    onMutate: async (carePlanId) => {
      console.log('useDeleteCarePlan: onMutate - Starting optimistic update for carePlanId:', carePlanId);
      if (!carePlanId) return;

      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['carePlans'] });
      
      // Get current data
      const previousCarePlans = queryClient.getQueryData(['carePlans', userKey, {}]);
      console.log('useDeleteCarePlan: onMutate - Previous care plans count:', previousCarePlans?.length || 0);

      // Check if the care plan exists before trying to delete it
      const carePlanExists = previousCarePlans?.some(plan => plan.care_plan_id === carePlanId);
      if (!carePlanExists) {
        throw new Error(`Care plan with ID ${carePlanId} not found in cache.`);
      }

      // Optimistically remove the care plan
      const updated = previousCarePlans?.filter(plan => plan.care_plan_id !== carePlanId) || [];
      console.log('useDeleteCarePlan: onMutate - Optimistic update, new count:', updated.length);

      queryClient.setQueryData(['carePlans', userKey, {}], updated);

      return { previousCarePlans }; // ✅ Needed for rollback in onError
    },
    onSuccess: () => {
      console.log('useDeleteCarePlan: Success - deletion completed');
    },
    onError: (err, carePlanId, context) => {
      console.error('useDeleteCarePlan: onError - Rolling back deletion for carePlanId:', carePlanId, err);
      if (context?.previousCarePlans) {
        queryClient.setQueryData(['carePlans', userKey, {}], context.previousCarePlans);
      }
    }
  });
};

// READ ACTION PLANS
export const useActionPlans = (filters = {}, options = {}) => {
  const userKey = getUserValue();
  
  return useQuery({
    queryKey: ['actionPlans', userKey, filters],
    queryFn: () => {
      console.log('useActionPlans: Fetching data for userKey:', userKey, 'filters:', filters);
      return fetchActionPlans(userKey, filters);
    },
    enabled: !!userKey,
    refetchOnWindowFocus: false, // Disable to prevent unnecessary requests during form interactions
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// CREATE ACTION PLAN
export const useCreateActionPlan = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (actionPlanData) => {
      console.log('useCreateActionPlan mutation called with:', actionPlanData);
      return createActionPlan(userKey, actionPlanData);
    },
    onMutate: async (actionPlanData) => {
      console.log('useCreateActionPlan: onMutate - Starting optimistic update');
      
      await queryClient.cancelQueries({ queryKey: ['actionPlans', userKey] });
      const previousActionPlans = queryClient.getQueryData(['actionPlans', userKey, {}]) || [];
      console.log('useCreateActionPlan: onMutate - Previous action plans count:', previousActionPlans.length);

      // Create a temporary action plan with a temporary ID
      const tempActionPlan = {
        action_plan_id: `temp-${Date.now()}`, // Temporary ID
        care_plan_id: actionPlanData.care_plan_id,
        action_name: actionPlanData.action_name,
        plan_type: actionPlanData.plan_type,
        recurrence_type: actionPlanData.recurrence_type,
        start_date: actionPlanData.start_date,
        end_date: actionPlanData.end_date,
        repeats_annually: actionPlanData.repeats_annually,
        action_mac_address: actionPlanData.action_mac_address,
        sensor_mac_address: actionPlanData.sensor_mac_address,
        actuator_type: actionPlanData.actuator_type,
        actuator_description: actionPlanData.actuator_description,
        is_active: actionPlanData.is_active || true,
        current_trigger_index: 0,
        next_execution_date: null,
        last_execution_date: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        username: userKey
      };

      // Optimistically add the new action plan
      const updated = [...previousActionPlans, tempActionPlan];
      console.log('useCreateActionPlan: onMutate - Optimistic update applied, new count:', updated.length);

      queryClient.setQueryData(['actionPlans', userKey, {}], updated);

      return { previousActionPlans, tempActionPlan }; // ✅ Needed for rollback in onError
    },
    onSuccess: (data, actionPlanData, context) => {
      console.log('useCreateActionPlan: Success, replacing temp action plan with real one:', data);
      
      // Replace the temporary action plan with the real one from the server
      const currentActionPlans = queryClient.getQueryData(['actionPlans', userKey, {}]) || [];
      const updated = currentActionPlans.map(plan => 
        plan.action_plan_id === context.tempActionPlan.action_plan_id 
          ? { ...plan, action_plan_id: data.action_plan_id }
          : plan
      );
      
      queryClient.setQueryData(['actionPlans', userKey, {}], updated);
    },
    onError: (err, actionPlanData, context) => {
      console.error('useCreateActionPlan: onError - Rolling back creation:', err);
      if (context?.previousActionPlans) {
        queryClient.setQueryData(['actionPlans', userKey, {}], context.previousActionPlans);
      }
    }
  });
};

// UPDATE ACTION PLAN
export const useUpdateActionPlan = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async ({ actionPlanId, updateData }) => {
      console.log('useUpdateActionPlan mutation called with:', { actionPlanId, updateData });
      return updateActionPlan(userKey, actionPlanId, updateData);
    },
    onMutate: async ({ actionPlanId, updateData }) => {
      console.log('useUpdateActionPlan: onMutate - Starting optimistic update for actionPlanId:', actionPlanId);
      if (!actionPlanId) return;

      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['actionPlans'] });
      
      // Get current data
      const previousActionPlans = queryClient.getQueryData(['actionPlans', userKey, {}]);
      console.log('useUpdateActionPlan: onMutate - Previous action plans count:', previousActionPlans?.length || 0);

      // Optimistically update the action plan
      const updated = previousActionPlans?.map(plan => 
        plan.action_plan_id === actionPlanId ? { ...plan, ...updateData } : plan
      ) || [];
      console.log('useUpdateActionPlan: onMutate - Optimistic update applied');

      // Update the cache
      queryClient.setQueryData(['actionPlans', userKey, {}], updated);

      return { previousActionPlans }; // ✅ Needed for rollback in onError
    },
    onSuccess: () => {
      console.log('useUpdateActionPlan: Success - update completed');
    },
    onError: (err, variables, context) => {
      console.error('useUpdateActionPlan: onError - Rolling back update for actionPlanId:', variables.actionPlanId, err);
      if (context?.previousActionPlans) {
        queryClient.setQueryData(['actionPlans', userKey, {}], context.previousActionPlans);
      }
    }
  });
};

// DELETE ACTION PLAN
export const useDeleteActionPlan = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (actionPlanId) => {
      console.log('useDeleteActionPlan mutation called with actionPlanId:', actionPlanId);
      return deleteActionPlan(userKey, actionPlanId);
    },
    onMutate: async (actionPlanId) => {
      console.log('useDeleteActionPlan: onMutate - Starting optimistic update for actionPlanId:', actionPlanId);
      if (!actionPlanId) return;

      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['actionPlans'] });
      
      // Get current data
      const previousActionPlans = queryClient.getQueryData(['actionPlans', userKey, {}]);
      console.log('useDeleteActionPlan: onMutate - Previous action plans count:', previousActionPlans?.length || 0);

      // Check if the action plan exists before trying to delete it
      const actionPlanExists = previousActionPlans?.some(plan => plan.action_plan_id === actionPlanId);
      if (!actionPlanExists) {
        throw new Error(`Action plan with ID ${actionPlanId} not found in cache.`);
      }

      // Optimistically remove the action plan
      const updated = previousActionPlans?.filter(plan => plan.action_plan_id !== actionPlanId) || [];
      console.log('useDeleteActionPlan: onMutate - Optimistic update, new count:', updated.length);

      queryClient.setQueryData(['actionPlans', userKey, {}], updated);

      return { previousActionPlans }; // ✅ Needed for rollback in onError
    },
    onSuccess: () => {
      console.log('useDeleteActionPlan: Success - deletion completed');
    },
    onError: (err, actionPlanId, context) => {
      console.error('useDeleteActionPlan: onError - Rolling back deletion for actionPlanId:', actionPlanId, err);
      if (context?.previousActionPlans) {
        queryClient.setQueryData(['actionPlans', userKey, {}], context.previousActionPlans);
      }
    }
  });
};

// READ ACTION TRIGGERS
export const useActionTriggers = (filters = {}, options = {}) => {
  const userKey = getUserValue();
  
  return useQuery({
    queryKey: ['actionTriggers', userKey, filters],
    queryFn: () => {
      console.log('useActionTriggers: Fetching data for userKey:', userKey, 'filters:', filters);
      return fetchActionTriggers(userKey, filters);
    },
    enabled: !!userKey,
    refetchOnWindowFocus: false, // Disable to prevent unnecessary requests during form interactions
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// CREATE ACTION TRIGGER
export const useCreateActionTrigger = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (triggerData) => {
      console.log('useCreateActionTrigger mutation called with:', triggerData);
      return createActionTrigger(userKey, triggerData);
    },
    onMutate: async (triggerData) => {
      console.log('useCreateActionTrigger: onMutate - Starting optimistic update');
      
      await queryClient.cancelQueries({ queryKey: ['actionTriggers', userKey] });
      const previousActionTriggers = queryClient.getQueryData(['actionTriggers', userKey, {}]) || [];
      console.log('useCreateActionTrigger: onMutate - Previous action triggers count:', previousActionTriggers.length);

      // Create a temporary action trigger with a temporary ID
      const tempActionTrigger = {
        trigger_id: `temp-${Date.now()}`, // Temporary ID
        action_plan_id: triggerData.action_plan_id,
        trigger_order: triggerData.trigger_order,
        trigger_type: triggerData.trigger_type,
        trigger_value: triggerData.trigger_value,
        month: triggerData.month,
        execution_date: triggerData.execution_date,
        execution_time: triggerData.execution_time,
        description: triggerData.description,
        is_enabled: triggerData.is_enabled || true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        username: userKey
      };

      // Optimistically add the new action trigger
      const updated = [...previousActionTriggers, tempActionTrigger];
      console.log('useCreateActionTrigger: onMutate - Optimistic update applied, new count:', updated.length);

      queryClient.setQueryData(['actionTriggers', userKey, {}], updated);

      return { previousActionTriggers, tempActionTrigger }; // ✅ Needed for rollback in onError
    },
    onSuccess: (data, triggerData, context) => {
      console.log('useCreateActionTrigger: Success, replacing temp action trigger with real one:', data);
      
      // Replace the temporary action trigger with the real one from the server
      const currentActionTriggers = queryClient.getQueryData(['actionTriggers', userKey, {}]) || [];
      const updated = currentActionTriggers.map(trigger => 
        trigger.trigger_id === context.tempActionTrigger.trigger_id 
          ? { ...trigger, trigger_id: data.trigger_id }
          : trigger
      );
      
      queryClient.setQueryData(['actionTriggers', userKey, {}], updated);
    },
    onError: (err, triggerData, context) => {
      console.error('useCreateActionTrigger: onError - Rolling back creation:', err);
      if (context?.previousActionTriggers) {
        queryClient.setQueryData(['actionTriggers', userKey, {}], context.previousActionTriggers);
      }
    }
  });
};

// UPDATE ACTION TRIGGER
export const useUpdateActionTrigger = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async ({ triggerId, updateData }) => {
      console.log('useUpdateActionTrigger mutation called with:', { triggerId, updateData });
      return updateActionTrigger(userKey, triggerId, updateData);
    },
    onMutate: async ({ triggerId, updateData }) => {
      console.log('useUpdateActionTrigger: onMutate - Starting optimistic update for triggerId:', triggerId);
      if (!triggerId) return;

      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['actionTriggers'] });
      
      // Get current data
      const previousActionTriggers = queryClient.getQueryData(['actionTriggers', userKey, {}]);
      console.log('useUpdateActionTrigger: onMutate - Previous action triggers count:', previousActionTriggers?.length || 0);

      // Optimistically update the action trigger
      const updated = previousActionTriggers?.map(trigger => 
        trigger.trigger_id === triggerId ? { ...trigger, ...updateData } : trigger
      ) || [];
      console.log('useUpdateActionTrigger: onMutate - Optimistic update applied');

      // Update the cache
      queryClient.setQueryData(['actionTriggers', userKey, {}], updated);

      return { previousActionTriggers }; // ✅ Needed for rollback in onError
    },
    onSuccess: () => {
      console.log('useUpdateActionTrigger: Success - update completed');
    },
    onError: (err, variables, context) => {
      console.error('useUpdateActionTrigger: onError - Rolling back update for triggerId:', variables.triggerId, err);
      if (context?.previousActionTriggers) {
        queryClient.setQueryData(['actionTriggers', userKey, {}], context.previousActionTriggers);
      }
    }
  });
};

// DELETE ACTION TRIGGER
export const useDeleteActionTrigger = () => {
  const queryClient = useQueryClient();
  const userKey = getUserValue();
  
  return useMutation({
    mutationFn: async (triggerId) => {
      console.log('useDeleteActionTrigger mutation called with triggerId:', triggerId);
      return deleteActionTrigger(userKey, triggerId);
    },
    onMutate: async (triggerId) => {
      console.log('useDeleteActionTrigger: onMutate - Starting optimistic update for triggerId:', triggerId);
      if (!triggerId) return;

      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: ['actionTriggers'] });
      
      // Get current data
      const previousActionTriggers = queryClient.getQueryData(['actionTriggers', userKey, {}]);
      console.log('useDeleteActionTrigger: onMutate - Previous action triggers count:', previousActionTriggers?.length || 0);

      // Check if the action trigger exists before trying to delete it
      const actionTriggerExists = previousActionTriggers?.some(trigger => trigger.trigger_id === triggerId);
      if (!actionTriggerExists) {
        throw new Error(`Action trigger with ID ${triggerId} not found in cache.`);
      }

      // Optimistically remove the action trigger
      const updated = previousActionTriggers?.filter(trigger => trigger.trigger_id !== triggerId) || [];
      console.log('useDeleteActionTrigger: onMutate - Optimistic update, new count:', updated.length);

      queryClient.setQueryData(['actionTriggers', userKey, {}], updated);

      return { previousActionTriggers }; // ✅ Needed for rollback in onError
    },
    onSuccess: () => {
      console.log('useDeleteActionTrigger: Success - deletion completed');
    },
    onError: (err, triggerId, context) => {
      console.error('useDeleteActionTrigger: onError - Rolling back deletion for triggerId:', triggerId, err);
      if (context?.previousActionTriggers) {
        queryClient.setQueryData(['actionTriggers', userKey, {}], context.previousActionTriggers);
      }
    }
  });
};

// READ CARE PLAN EVENTS
export const useCarePlanEvents = (filters = {}, options = {}) => {
  const userKey = getUserValue();
  
  return useQuery({
    queryKey: ['carePlanEvents', userKey, filters],
    queryFn: () => fetchCarePlanEvents(userKey, filters),
    enabled: !!userKey,
    refetchOnWindowFocus: false, // Disable to prevent unnecessary requests during form interactions
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// READ CARE PLAN SCHEDULE
export const useCarePlanSchedule = (filters = {}, options = {}) => {
  const userKey = getUserValue();
  
  return useQuery({
    queryKey: ['carePlanSchedule', userKey, filters],
    queryFn: () => fetchCarePlanSchedule(userKey, filters),
    enabled: !!userKey,
    refetchOnWindowFocus: false, // Disable to prevent unnecessary requests during form interactions
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options,
  });
};

// READ WATERING SUMMARY (R3) — current state + last completed run for one pump/action.
// Disabled (no network calls) until REACT_APP_WATERING_EVENTS_SERVER is set, so this is safe
// to ship before the Pi backend implements the endpoint.
// See READMES/irrigation/WATERING_EVENTS_BACKEND_SPEC_README.md
export const useWateringSummary = (actionMac, options = {}) => {
  return useQuery({
    queryKey: ['wateringSummary', actionMac],
    queryFn: () => fetchWateringSummary(actionMac),
    enabled: !!actionMac && isWateringEventsEnabled() && (options.enabled !== false),
    refetchInterval: 30000, // keep "watering now" reasonably live
    refetchOnWindowFocus: true,
    staleTime: 15000,
    ...options,
  });
};

// READ WATERING EVENTS (R3) — history list for an action and/or sensor. Same graceful gating.
export const useWateringEvents = (filters = {}, options = {}) => {
  const { actionMac, sensorMac } = filters;
  return useQuery({
    queryKey: ['wateringEvents', filters],
    queryFn: () => fetchWateringEvents(filters),
    enabled: (!!actionMac || !!sensorMac) && isWateringEventsEnabled() && (options.enabled !== false),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    ...options,
  });
};

// Clear all React Query cache and localStorage when needed (e.g. on logout)
export const clearCache = async (queryClient) => {
  try {
    // We need to maintain MQTT synchronization by dispatching the event
    window.dispatchEvent(new CustomEvent('mqttAddressesChanged'));
    
    // Clear the React Query cache
    queryClient?.clear();
    
    // Clear localStorage 
    localStorage.clear();
  } catch (error) {
    console.error('Error during cache clearing:', error);
    // Still clear localStorage even if server sync fails
    localStorage.clear();
  }
};

// Helper function to get user value from localStorage
export const getUserValue = () => {
  try {
    const userValItem = localStorage.getItem('userVal');
    if (userValItem) {
      const parsed = JSON.parse(userValItem);
      return parsed.data;
    }
    return null;
  } catch (error) {
    console.error('Error retrieving user value:', error);
    return null;
  }
};

// Set user value for React Query
export const setUserValue = (username) => {
  if (username) {
    // Check if the current value is already set to prevent unnecessary updates
    const currentValue = getUserValue();
    if (currentValue !== username) {
      localStorage.setItem('userVal', JSON.stringify({ data: username }));
    }
  }
};